import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "../local-storage.service";
import { BenutzerModels } from "../models/benutzer-models";
import { SwissAppConfigModels } from "../models/swissappconfig-models";
import { HelperService } from "./helper.service";
import { DokumentModels } from "../models/dokument-models";
import { UserAuthenticationService } from "./user-authentication.service";
import { BehaviorSubject, firstValueFrom, from, Observable, of, ReplaySubject, shareReplay, tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, switchMap } from "rxjs/operators";
import SwissAppUserConfig = SwissAppConfigModels.SwissAppUserConfig;
import DefaultWebUserConfig = BenutzerModels.DefaultWebUserConfig;
import SwissAppConfig = SwissAppConfigModels.SwissAppConfig;
import { SWISS_WEB_CONFIG_DB_NAME } from "../models/local-storage-names";

@Injectable({
    providedIn: "root",
})
export class SwissSettingsService {
    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
    public swissAppConfigLoaded = false;
    public swissAppUserConfigLoaded = false;

    private storageWebConfig = "SwissWebConfigDB";
    private configsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private webUserConfig$: BehaviorSubject<BenutzerModels.WebUserConfig> = new BehaviorSubject(BenutzerModels.DefaultWebUserConfig());
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private swissAppUserConfig$: ReplaySubject<SwissAppUserConfig> = new ReplaySubject(1);

    // TODO 2025-12-08 https://swissjira.atlassian.net/browse/EN-4208 migrate configs into Anwender for consistency (also why is userappconfig basically just Service App Config?)
    private swissAppConfig?: SwissAppConfigModels.SwissAppConfig;
    private swissAppUserConfig?: SwissAppConfigModels.SwissAppUserConfig;
    private swissAppReports: SwissAppConfigModels.SwissAppReport[] = [];
    private swissAppFileReports: DokumentModels.SwissAppFileReport[] = [];

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
    private reloadSwissAppSettings?: () => void;
    private reloadSwissAppReports?: () => void;

    constructor(
        private localStorageService: LocalStorageService,
        private http: HttpClient,
        private userAuthService: UserAuthenticationService,
        private helperService: HelperService,
    ) {
        this.helperService
            .getSwissWebServiceConfig$()
            .pipe(
                takeUntilDestroyed(),
                tap((config) => {
                    this.webServiceConfig = config;
                }),
            )
            .subscribe();
        this.userAuthService
            .getCurrentUser()
            .pipe(
                takeUntilDestroyed(),
                tap(async (user) => {
                    await this.reloadUserConfig();
                }),
            )
            .subscribe();
    }

    async reloadUserConfig(): Promise<void> {
        const conf = await this.getUserConfig();
        this.swissAppUserConfig$.next(conf);
    }

    // TODO 2025-12-08 https://swissjira.atlassian.net/browse/EN-4208 migrate configs into Anwender for consistency (also why is userappconfig basically just Service App Config?)
    public async getUserConfig(): Promise<SwissAppUserConfig> {
        return this.loadUserConfig(false);
    }

    public async loadUserConfig(initLoad = true): Promise<SwissAppUserConfig> {
        const swissAppConfig = await firstValueFrom(this.getServiceAppConfig());
        const swissAppUserConfig = await firstValueFrom(this.getServiceAppUserConfig());

        const conf = {
            mandantID: swissAppUserConfig.mandantID,
            employeeID: swissAppUserConfig.employeeID,
            timeGridStart: swissAppUserConfig.timeGridStart || swissAppConfig.timeGridStart,
            timeGridEnd: swissAppUserConfig.timeGridEnd || swissAppConfig.timeGridEnd,
            timeGridWorkDayStart: swissAppUserConfig.timeGridWorkDayStart || swissAppConfig.timeGridWorkDayStart,
            timeGridWorkDayEnd: swissAppUserConfig.timeGridWorkDayEnd || swissAppConfig.timeGridWorkDayEnd,
            orderFilterObjView6: swissAppUserConfig.orderFilterObjView6 || swissAppConfig.orderFilterObjView6,
            startPage: swissAppUserConfig.startPage !== null ? swissAppUserConfig.startPage : swissAppConfig.startPage,
            objViewConfig: swissAppUserConfig.objViewConfig !== null ? swissAppUserConfig.objViewConfig : swissAppConfig.objViewConfig,
            uploadNecessary: swissAppUserConfig.uploadNecessary !== null ? swissAppUserConfig.uploadNecessary : swissAppConfig.uploadNecessary,
            showObjectStatusColorInTree:
                swissAppUserConfig.showObjectStatusColorInTree !== null ? swissAppUserConfig.showObjectStatusColorInTree : swissAppConfig.showObjectStatusColorInTree,
        };

        // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
        // legacy code support for waitForConfig need to analyse if i can remove more
        if (initLoad) {
            this.swissAppConfig = swissAppConfig;
            this.swissAppUserConfig = swissAppUserConfig;
            this.swissAppUserConfigLoaded = true;
            this.swissAppConfigLoaded = true;
            await this.loadReports();
            this.configsLoaded$.next(true);
            this.swissAppUserConfig$.next(conf);
        }

        return conf;
    }

    public getSwissAppUserConfig$(): Observable<SwissAppUserConfig> {
        return this.swissAppUserConfig$.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
    // quite sure the consumers also track unused and unuseful data
    registerSwissAppSettings(fn: () => void): void {
        this.reloadSwissAppSettings = fn;
        // Falls die Registrierung erst stattfindet, wenn die Config's schon geladen sind,
        // dann kann die registrierte Funktion sofort aufgerufen werden.
        if (this.swissAppConfigLoaded && this.swissAppUserConfigLoaded) {
            this.reloadSwissAppSettings();
        }
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
    // quite sure the consumers also track unused and unuseful data
    registerSwissAppReports(fn: () => void): void {
        this.reloadSwissAppReports = fn;
    }

    public getConfigsLoaded$(): Observable<boolean> {
        return this.configsLoaded$.asObservable();
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
    // quite sure the consumers also track unused and unuseful data
    public async waitForSwissAppConfigs(): Promise<boolean> {
        const wert = await this.loadSwissAppConfig();
        if (wert) {
            await this.loadSwissAppUserConfig();
            return true;
        } else {
            return false;
        }
    }

    public async loadSwissAppConfig(): Promise<boolean> {
        return new Promise((resolve) => {
            this.getServiceAppConfig().subscribe({
                next: (res) => {
                    this.swissAppConfig = res;
                    // Swiss-App-Config in Indexed-DB speichern
                    this.localStorageService.add("SwissAppConfig", this.swissAppConfig, this.storageWebConfig, false, true);
                    this.swissAppConfigLoaded = true;
                    resolve(true);
                },
                error: () => {
                    resolve(false);
                },
            });
        });
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
    // quite sure the consumers also track unused and unuseful data
    getSwissAppConfig(): SwissAppConfigModels.SwissAppConfig {
        if (!this.swissAppConfig) {
            throw Error();
        }
        return this.swissAppConfig;
    }

    async reloadWebUserConfig(): Promise<void> {
        await this.loadWebUserConfig();
    }

    public async updateWebUserConfig(webUserConfig: BenutzerModels.WebUserConfig): Promise<void> {
        await this.localStorageService.add("webuserconfig", webUserConfig, this.storageWebConfig);
        this.webUserConfig$.next(webUserConfig);
    }

    // Ermittelt die aktuellen Webeinstellungen des Benutzers
    getWebConfig(): BenutzerModels.WebUserConfig {
        return this.webUserConfig$.getValue();
    }

    getWebConfig$(): BehaviorSubject<BenutzerModels.WebUserConfig> {
        return this.webUserConfig$;
    }

    getServiceAppConfig(): Observable<SwissAppConfigModels.SwissAppConfig> {
        if (!this.webServiceConfig) {
            throw Error();
        }
        const url = this.webServiceConfig.anwUrl;
        return this.http
            .get<SwissAppConfigModels.SwissAppConfig>(
                url + "/SwissAppConfig?mandant=" + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID,
                this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo),
            )
            .pipe(
                switchMap(async (config) => {
                    await this.localStorageService.add("SwissAppConfig", config, SWISS_WEB_CONFIG_DB_NAME, false, true);
                    return config;
                }),
                catchError(() => {
                    return from(this.localStorageService.get<SwissAppConfig>("SwissAppConfig", SWISS_WEB_CONFIG_DB_NAME, true));
                }),
            );
    }

    getServiceAppUserConfig(): Observable<SwissAppConfigModels.SwissAppUserConfig> {
        if (this.helperService.isWebServiceAvailable()) {
            const user = this.userAuthService.getCurrentAuthenticatedUser();
            if (!this.webServiceConfig || !user) {
                throw Error();
            }
            const url = this.webServiceConfig.anwUrl;
            return this.http.get<SwissAppConfigModels.SwissAppUserConfig>(
                url + "/SwissAppUserConfig?mandant=" + user.mandatorID + "&employeeID=" + user.employeeID,
                this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo),
            );
        } else {
            return new Observable((subscriber) => {
                this.localStorageService.get("SwissAppUserConfig", this.storageWebConfig).then((companies: any) => subscriber.next(companies));
            });
        }
    }

    async getServiceAppReports(): Promise<SwissAppConfigModels.SwissAppReport[]> {
        const user = this.userAuthService.getCurrentAuthenticatedUser();
        if (!this.webServiceConfig || !user) {
            throw Error();
        }
        const url = this.webServiceConfig.anwUrl;
        return new Promise((resolve) => {
            this.http
                .get<
                    SwissAppConfigModels.SwissAppReport[]
                >(url + "/Reporte?mandant=" + user.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo))
                .subscribe({
                    next: async (reports) => {
                        await this.localStorageService.add("SwissAppReports", reports, this.storageWebConfig);
                        resolve(reports);
                    },
                    error: async () => {
                        resolve(await this.localStorageService.get("SwissAppReports", this.storageWebConfig, false, []));
                    },
                });
        });
    }

    /* Benutzereinstellungen laden */
    public async loadSwissAppUserConfig(): Promise<boolean> {
        return new Promise((resolve) => {
            this.getServiceAppUserConfig().subscribe({
                next: (res) => {
                    this.swissAppUserConfig = res;
                    // Swiss-App-Config in Indexed-DB speichern
                    this.localStorageService.add("SwissAppUserConfig", this.swissAppUserConfig, this.storageWebConfig);
                    this.swissAppUserConfigLoaded = true;
                    resolve(true);
                },
                error: (err) => console.log("Fehler in loadSwissAppUserConfig:", err),
            });
        });
    }

    public getObjektReportFile(): DokumentModels.SwissAppFileReport | undefined {
        if (this.swissAppFileReports && this.swissAppFileReports.length > 0) {
            return this.swissAppFileReports[0];
        } else {
            return undefined;
        }
    }

    getTemplate(filePath: string): Observable<any> {
        if (!this.webServiceConfig) {
            throw Error();
        }
        const url = this.webServiceConfig.dokuUrl;
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        return this.http.get<any>(url + "/DownloadTemplateFile?filePath=" + filePath, { headers, responseType: "blob" as "json" });
    }

    getSwissAppUserConfig(): SwissAppConfigModels.SwissAppUserConfig {
        if (!this.swissAppUserConfig) {
            throw Error();
        }
        return this.swissAppUserConfig;
    }

    async setStartPage(startPage: number): Promise<void> {
        this.swissAppUserConfig = await firstValueFrom(this.getServiceAppUserConfig());
        if (this.swissAppUserConfig) {
            this.swissAppUserConfig.startPage = startPage;
            this.swissAppUserConfig.uploadNecessary = true;
            await this.localStorageService.add("SwissAppUserConfig", this.swissAppUserConfig, this.storageWebConfig);
            await this.uploadSwissAppUserConfig();
        } else {
            throw Error();
        }
    }

    getObjViewConfig(): number {
        if (this.swissAppConfig && this.swissAppUserConfig) {
            if (this.swissAppUserConfig.objViewConfig) {
                return this.swissAppUserConfig.objViewConfig;
            }
            return this.swissAppConfig.objViewConfig;
        }
        return 0;
    }

    setTimeGrid(start: string, end: string): void {
        if (this.swissAppUserConfig) {
            this.swissAppUserConfig.timeGridStart = start;
            this.swissAppUserConfig.timeGridEnd = end;
            this.swissAppUserConfig.uploadNecessary = true;
            this.localStorageService.add("SwissAppUserConfig", this.swissAppUserConfig, this.storageWebConfig);
            this.uploadSwissAppUserConfig();
        }
    }

    setTimeGridWorkDay(start: string, end: string): void {
        if (this.swissAppUserConfig) {
            this.swissAppUserConfig.timeGridWorkDayStart = start;
            this.swissAppUserConfig.timeGridWorkDayEnd = end;
            this.swissAppUserConfig.uploadNecessary = true;
            this.localStorageService.add("SwissAppUserConfig", this.swissAppUserConfig, this.storageWebConfig);
            this.uploadSwissAppUserConfig();
        }
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 quite sure those can and should go
    // Steuer den Raster-Start
    getTimeGridStart(): string {
        if (this.swissAppUserConfig) {
            if (this.swissAppUserConfig.timeGridStart) {
                return this.swissAppUserConfig.timeGridStart;
            } else if (this.swissAppConfig?.timeGridStart) {
                return this.swissAppConfig.timeGridStart;
            }
        } else {
            if (this.swissAppConfig?.timeGridStart) {
                return this.swissAppConfig.timeGridStart;
            }
        }
        // Standard-Wert zurückgeben
        return "05:00";
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 quite sure those can and should go
    // Steuer das Raster-Ende
    getTimeGridEnd(): string {
        if (this.swissAppUserConfig) {
            if (this.swissAppUserConfig.timeGridEnd) {
                return this.swissAppUserConfig.timeGridEnd;
            } else if (this.swissAppConfig?.timeGridEnd) {
                return this.swissAppConfig.timeGridEnd;
            }
        } else {
            if (this.swissAppConfig?.timeGridStart) {
                return this.swissAppConfig.timeGridStart;
            }
        }
        return "20:00";
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 quite sure those can and should go
    getTimeGridWorkDayStart(): string {
        if (this.swissAppUserConfig) {
            if (this.swissAppUserConfig.timeGridWorkDayStart) {
                return this.swissAppUserConfig.timeGridWorkDayStart;
            } else if (this.swissAppConfig?.timeGridWorkDayStart) {
                return this.swissAppConfig.timeGridWorkDayStart;
            }
        } else {
            if (this.swissAppConfig?.timeGridWorkDayStart) {
                return this.swissAppConfig.timeGridWorkDayStart;
            }
        }
        // Standard-Wert zurückgeben
        return "07:00";
    }

    // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 quite sure those can and should go
    getTimeGridWorkDayEnd(): string {
        if (this.swissAppUserConfig) {
            if (this.swissAppUserConfig.timeGridWorkDayEnd) {
                return this.swissAppUserConfig.timeGridWorkDayEnd;
            } else if (this.swissAppConfig?.timeGridWorkDayEnd) {
                return this.swissAppConfig.timeGridWorkDayEnd;
            }
        } else {
            if (this.swissAppConfig?.timeGridWorkDayEnd) {
                return this.swissAppConfig.timeGridWorkDayEnd;
            }
        }
        return "18:00";
    }

    async uploadSwissAppUserConfig(): Promise<void> {
        if (!this.webServiceConfig) {
            throw Error();
        }
        const url = this.webServiceConfig.anwUrl;
        this.http
            .post<SwissAppConfigModels.SwissAppUserConfig>(
                url + "/UpdateUserConfig",
                this.swissAppUserConfig,
                this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo),
            )
            .subscribe({
                next: () => {
                    if (this.swissAppUserConfig) {
                        this.swissAppUserConfig.uploadNecessary = false;
                    }
                },
                error: (err) => {
                    console.log("SwissAppUserConfig konnte nicht aktualisiert werden....", err);
                },
            });
    }

    public async loadReports(): Promise<void> {
        this.swissAppReports = await this.getServiceAppReports();
        await this.downloadReportFiles();
    }

    public setShowStatusColorInTreeview(showColor: boolean): Observable<any> {
        const user = this.userAuthService.getCurrentAuthenticatedUser();
        if (!user || !user.employeeID || !user.mandatorID) {
            throw new Error("No user is currently authenticated!");
        }

        const url = this.webServiceConfig!.anwUrl;
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        return this.http.post<any>(
            url + "/UpdateShowStatusColorInTree",
            {
                showStatusColorInTreeRequest: showColor,
                employeeID: user.employeeID,
                mandantorID: user.mandatorID,
            },
            { headers },
        );
    }

    private async loadWebUserConfig(): Promise<void> {
        const value = await this.localStorageService.get("webuserconfig", this.storageWebConfig, false, DefaultWebUserConfig());
        this.webUserConfig$.next(value);
    }

    private async downloadReportFiles(): Promise<void> {
        this.swissAppReports.forEach((rep) => {
            if (rep.pdfTemplatePath) {
                this.getTemplate(rep.pdfTemplatePath).subscribe({
                    next: (res) => {
                        const swissAppFileRep: DokumentModels.SwissAppFileReport = {
                            mandatorID: rep.mandator,
                            iD_SwissAppReport: rep.iD_SwissAppReport,
                            pdfFileTemplate: res,
                        };
                        this.swissAppFileReports.push(swissAppFileRep);
                        this.localStorageService.add("SwissAppReportFiles", this.swissAppFileReports, this.storageWebConfig);
                    },
                    error: async () => {
                        this.swissAppFileReports = await this.localStorageService.get("SwissAppReportFiles", this.storageWebConfig, false, []);
                    },
                });
            }
        });

        // TODO 26-02-24 https://swissjira.atlassian.net/browse/EN-4512 remove those registered function and use observable
        if (this.reloadSwissAppReports) {
            this.reloadSwissAppReports();
        }
    }
}
